package com.partejar.app.feature.equipe.presentation

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.partejar.app.core.security.BiometricAuthenticationResult
import com.partejar.app.feature.equipe.data.EquipeDataSource
import com.partejar.app.feature.equipe.data.EquipeDataException
import com.partejar.app.feature.equipe.data.EquipeMemberMissingException
import com.partejar.app.feature.equipe.data.EquipeMember
import com.partejar.app.feature.equipe.data.TeamInviteInput
import com.partejar.app.feature.equipe.data.TeamInviteResult
import com.partejar.app.feature.equipe.data.TeamMemberUpdate
import com.partejar.app.feature.equipe.data.ProfessionalRegistration
import com.partejar.app.feature.entitlements.data.CommercialConstraintException
import com.partejar.app.feature.entitlements.data.CommercialState
import com.partejar.app.feature.entitlements.data.WorkspaceEntitlement
import com.partejar.app.feature.entitlements.presentation.EntitlementCoordinator
import com.partejar.app.feature.workspace.presentation.commercialMessage
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

sealed interface EquipeUiState {
    data object Loading : EquipeUiState
    data class Content(val members: List<EquipeMember>) : EquipeUiState
    data class Error(val message: String) : EquipeUiState
}

sealed interface EquipeActionState {
    data object Idle : EquipeActionState
    data object Submitting : EquipeActionState
    data object Authenticating : EquipeActionState
    data object AuthenticationCancelled : EquipeActionState
    data class InviteCreated(val result: TeamInviteResult) : EquipeActionState
    data class MemberUpdated(val member: EquipeMember) : EquipeActionState
    data class MemberRemoved(val memberId: Long, val userId: Long) : EquipeActionState
    data class Error(val message: String) : EquipeActionState
}

class EquipeViewModel private constructor(
    private val dataSource: EquipeDataSource,
    private val entitlementCoordinator: EntitlementCoordinator? = null,
    private val scopeOverride: CoroutineScope?,
    @Suppress("UNUSED_PARAMETER") constructorMarker: Unit
) : ViewModel() {
    constructor(dataSource: EquipeDataSource) : this(dataSource, null, null, Unit)
    constructor(dataSource: EquipeDataSource, entitlementCoordinator: EntitlementCoordinator) : this(dataSource, entitlementCoordinator, null, Unit)
    internal constructor(dataSource: EquipeDataSource, scope: CoroutineScope) : this(dataSource, null, scope, Unit)

    private val scope get() = scopeOverride ?: viewModelScope
    private val _uiState = MutableStateFlow<EquipeUiState>(EquipeUiState.Loading)
    val uiState: StateFlow<EquipeUiState> = _uiState.asStateFlow()
    private val _actionState = MutableStateFlow<EquipeActionState>(EquipeActionState.Idle)
    val actionState: StateFlow<EquipeActionState> = _actionState.asStateFlow()
    private val _registrations = MutableStateFlow<List<ProfessionalRegistration>>(emptyList())
    val registrations: StateFlow<List<ProfessionalRegistration>> = _registrations.asStateFlow()
    val commercialState: StateFlow<CommercialState<WorkspaceEntitlement>>? get() = entitlementCoordinator?.workspace

    init { load(); entitlementCoordinator?.refreshWorkspace() }

    fun load() {
        _uiState.value = EquipeUiState.Loading
        scope.launch {
            _uiState.value = try {
                EquipeUiState.Content(dataSource.list())
            } catch (error: CancellationException) {
                throw error
            } catch (_: Throwable) {
                EquipeUiState.Error("Não foi possível carregar a equipe.")
            }
        }
    }

    fun invite(input: TeamInviteInput) = launchAction {
        if (input.name.isBlank() || input.email.isBlank()) throw IllegalArgumentException()
        val entitlement = (entitlementCoordinator?.workspace?.value as? CommercialState.Available)?.value
        if (entitlement?.memberLimit?.let { entitlement.membersUsed >= it } == true) {
            throw CommercialConstraintException("LIMITE_MEMBROS_ATINGIDO")
        }
        EquipeActionState.InviteCreated(dataSource.invite(input)).also { entitlementCoordinator?.refreshWorkspace() }
    }

    fun update(member: EquipeMember, update: TeamMemberUpdate) = launchAction {
        val updated = dataSource.update(member.id, update)
        val current = (_uiState.value as? EquipeUiState.Content)?.members.orEmpty()
        _uiState.value = EquipeUiState.Content(current.map { if (it.id == updated.id) updated else it })
        entitlementCoordinator?.refreshWorkspace()
        EquipeActionState.MemberUpdated(updated)
    }

    fun remove(
        member: EquipeMember,
        authenticate: suspend () -> BiometricAuthenticationResult
    ) {
        _actionState.value = EquipeActionState.Authenticating
        scope.launch {
            _actionState.value = try {
                when (authenticate()) {
                    BiometricAuthenticationResult.Success -> {
                        dataSource.update(
                            member.id,
                            TeamMemberUpdate(
                                profileCode = member.profileCode,
                                professionalCategory = member.professionalCategory,
                                professionalRegistrationId = null,
                                active = false
                            )
                        )
                        val current = (_uiState.value as? EquipeUiState.Content)?.members.orEmpty()
                        _uiState.value = EquipeUiState.Content(current.filterNot { it.id == member.id })
                        entitlementCoordinator?.refreshWorkspace()
                        EquipeActionState.MemberRemoved(member.id, member.userId)
                    }
                    BiometricAuthenticationResult.Cancelled -> EquipeActionState.AuthenticationCancelled
                    BiometricAuthenticationResult.Failed -> EquipeActionState.Error(
                        "Não foi possível confirmar sua identidade. Tente novamente."
                    )
                    BiometricAuthenticationResult.Unavailable -> EquipeActionState.Error(
                        "Configure um bloqueio seguro no dispositivo para remover integrantes."
                    )
                }
            } catch (error: CancellationException) {
                throw error
            } catch (error: EquipeMemberMissingException) {
                load()
                EquipeActionState.Error(error.message.orEmpty())
            } catch (error: CommercialConstraintException) {
                EquipeActionState.Error(commercialMessage(error.code))
            } catch (error: EquipeDataException) {
                EquipeActionState.Error(error.message ?: "Não foi possível remover esta pessoa.")
            } catch (_: Throwable) {
                EquipeActionState.Error("Não foi possível remover esta pessoa.")
            }
        }
    }
    fun loadRegistrations(memberId: Long) {
        _registrations.value = emptyList()
        scope.launch { _registrations.value = runCatching { dataSource.registrations(memberId) }.getOrDefault(emptyList()) }
    }

    fun clearAction() { _actionState.value = EquipeActionState.Idle }

    private fun launchAction(block: suspend () -> EquipeActionState) {
        _actionState.value = EquipeActionState.Submitting
        scope.launch {
            _actionState.value = try {
                block()
            } catch (error: CancellationException) {
                throw error
            } catch (error: CommercialConstraintException) {
                EquipeActionState.Error(commercialMessage(error.code))
            } catch (error: EquipeDataException) {
                EquipeActionState.Error(error.message ?: "Não foi possível salvar as alterações.")
            } catch (_: Throwable) {
                EquipeActionState.Error("Não foi possível salvar as alterações.")
            }
        }
    }
}
